import React, { useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import SydButton from '../../../../../commonDesign/Button'
import { useAdvisorFactors, useAdvisorResetFactorsMutation } from '../../../../../../api/users'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'

const ResetAuthAction = ({ user, isLoading: _isLoading }) => {
  const [showModal, setShowModal] = useState(false)
  const { mutateAsync: resetAuth, isLoading: resetIsLoading } = useAdvisorResetFactorsMutation()
  const { data: factorResponse, isLoading: isLoadingFactors } = useAdvisorFactors(user?.userId)

  const validFactors = useMemo(() => factorResponse?.factors?.filter(factor => factor.factorType !== 'email') ?? [], [factorResponse?.factors])
  const isLoading = useMemo(() => resetIsLoading || _isLoading || isLoadingFactors, [resetIsLoading, _isLoading, isLoadingFactors])

  const confirmHandler = useCallback(async () => {
    try {
      await resetAuth(user)
      setShowModal(false)
    } catch (err) {
      console.error(err)
      alert('There was an unexpected issue resetting the user')
    }
  }, [user, resetAuth])
  const disabled = useMemo(() => isLoading || !validFactors?.length, [validFactors, isLoading])

  const description = useMemo(() => {
    if (_isLoading || isLoadingFactors) return ''

    if (!validFactors?.length) {
      return 'The user has no MFA factors to reset.'
    }
    return 'This will remove all MFA factors for this advisor. They will be prompted to set up MFA again on their next login.'
  }, [_isLoading, isLoadingFactors, validFactors])

  const factorNames = useMemo(() => validFactors.map(({ factorType, vendorName }) => {
    if (factorType === 'token:software:totp') {
      return ['OTP Authenticator', vendorName?.length ? `(${vendorName})` : ''].join(' ')
    }
    return factorType
  }), [validFactors])

  return (
    <div className='__row'>
      <div>
        <SydButton
          size='sm'
          icon='sync'
          variant='outline'
          onClick={() => setShowModal(true)}
          processing={isLoading}
          disabled={disabled}
        >
          {(isLoadingFactors || _isLoading) ? 'Loading' : 'Reset Authentication Factors'}
        </SydButton>
      </div>
      <div className={clsx({ __disabled: disabled })}>
        {description}

        {!!factorNames?.length && (
          <div className='flex gap-2 mt-2 items-center'>
            <span>Current Factors:</span>
            {factorNames?.map(name => (
              <span key={name} className='py-1 px-2 border bg-surface-400 font-size-xs radius-md'>{name}</span>
            ))}
          </div>
        )}
      </div>

      <SydModal
        open={showModal}
        title='Reset Authentication'
        subtitle={`Are you sure you want to reset authentication factors for ${user?.firstName} ${user?.lastName}?  They will be prompted to set up their authentication factors again on their next login.`}
      >
        <SydModalActions>
          <SydButton
            variant='primary'
            priority='warning'
            onClick={() => setShowModal(false)}
            processing={resetIsLoading}
          >
            Cancel
          </SydButton>
          <SydButton
            variant='primary'
            onClick={confirmHandler}
            processing={resetIsLoading}
          >
            Confirm
          </SydButton>
        </SydModalActions>
      </SydModal>
    </div>
  )
}

ResetAuthAction.propTypes = {
  user: PropTypes.object,
  isLoading: PropTypes.bool
}

export default ResetAuthAction
